import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { select, Store } from '@ngrx/store';
import { AppState } from 'src/app/core/reducers';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { LayoutUtilsService, QueryParamsModel } from '../../../../core/_base/crud';
import { fromEvent, merge, of, Subscription } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { debounceTime, distinctUntilChanged, skip, take, tap } from 'rxjs/operators';
import {
  FollowUpDatasource,
  FollowUpModel,
  FollowUpPageRequested,
  selectFollowUpPageLastQuery,
} from '../../../../core/follow-up';
import { SubheaderService } from '../../../../core/_base/layout';
import { FollowUpDialogComponent } from '../../follow-up/follow-up-dialog/follow-up-dialog.component';
import { ContactsService } from '../../../../core/lists/_services';
import { TaskService } from '../../../../core/timeline/_services';
import { FormControl } from '@angular/forms';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import * as moment from 'moment';
import { UserService } from '../../../../core/auth/_services';
import { currentUser } from '../../../../core/auth';
import { CommentsDialogComponent } from '../../../partials/content/general/comments-dialog/comments-dialog.component';
import { FollowUpService } from '../../../../core/services/follow-up.service';

@Component({
  selector: 'kt-followup-list-view',
  templateUrl: './followup-list.component.html',
  styleUrls: ['./followup-list.component.scss'],
})
export class FollowupListComponent implements OnInit, OnDestroy {
  dataSource: FollowUpDatasource;
  displayedColumns = [
    'contact',
    'type',
    'name',
    'attempts',
    'dueDate',
    'createdBy',
    'createdOn',
    'status',
    'comments',
  ];
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild('sort1', { static: true }) sort: MatSort;
  @ViewChild('userEle', { static: true }) userEle: ElementRef;
  lastQuery: QueryParamsModel;
  followupResult: FollowUpModel[] = [];
  private subscriptions: Subscription[] = [];
  allContacts = [];
  allUsers = [];
  maxDate: any;
  minDate: any;
  startDateCreated: FormControl;
  toDateCreated: FormControl;
  filterStatus = 'true';
  userSelected = null;
  userOptions = [];
  teamsFollowUp = false;
  constructor(
    private followUpService: FollowUpService,
    private userService: UserService,
    private contactsService: ContactsService,
    public dialog: MatDialog,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private layoutUtilsService: LayoutUtilsService,
    private store: Store<AppState>,
    public subheaderService: SubheaderService,
    private cdr: ChangeDetectorRef,
    private taskService: TaskService,
  ) {
    this.activatedRoute.queryParams.subscribe((params) => {
      this.teamsFollowUp = params.isTeamFollowUp ? params.isTeamFollowUp : null;
    });
    this.startDateCreated = new FormControl(null);
    this.toDateCreated = new FormControl(null);
    this.maxDate = new Date();
    this.userAutoComplete('');
  }

  ngOnInit() {
    this.subheaderService.setBreadcrumbs([{ title: 'Follow-Up List', page: '/followup/list' }]);
    fromEvent(this.userEle.nativeElement, 'keyup')
      .pipe(
        debounceTime(600),
        tap((name) => {
          if (name) {
            this.userAutoComplete(this.userEle.nativeElement.value);
          }
        }),
      )
      .subscribe();
    const paginatorSubscriptions = merge(this.sort.sortChange, this.paginator.page)
      .pipe(tap(() => this.loadFollowUpList()))
      .subscribe();
    this.subscriptions.push(paginatorSubscriptions);
    this.dataSource = new FollowUpDatasource(this.store);
    const entitiesSubscription = this.dataSource.entitySubject
      .pipe(skip(1), distinctUntilChanged())
      .subscribe((res) => {
        this.followupResult = res;

        const contactsId = this.followupResult
          .map((e) => e.contact_id)
          .filter((element) => {
            return element !== null;
          });
        this.contactsService.getContactsByIds(contactsId).subscribe((res1) => {
          if (!res || res.length === 0) return of({});
          this.allContacts = res1.data.reduce((out, current) => {
            return { ...out, [current.id]: current };
          }, {});
          this.cdr.detectChanges();
        });

        const usersId = this.followupResult
          .map((e) => e.created_by)
          .filter((element) => {
            return element !== null;
          });
        this.taskService.getUsersbyId(usersId).subscribe((res2) => {
          if (res2.pageOfItems && res2.pageOfItems.length !== 0) {
            this.allUsers = res2.pageOfItems.reduce((out, current) => {
              return { ...out, [current.id]: current };
            }, {});
            this.cdr.detectChanges();
          }
        });
      });
    this.subscriptions.push(entitiesSubscription);
    const lastQuerySubscription = this.store
      .pipe(select(selectFollowUpPageLastQuery))
      .subscribe((res) => (this.lastQuery = res));
    this.subscriptions.push(lastQuerySubscription);
    const userSubscription = this.store.pipe(select(currentUser), take(1)).subscribe((res) => {
      if (res.id && res.companyID && !this.teamsFollowUp) {
        this.userSelected = res;
        this.userEle.nativeElement.value = res ? res.userName : '';
      }
    });
    this.subscriptions.push(userSubscription);
    const routeSubscription = this.activatedRoute.queryParams.subscribe(() => {
      this.loadFollowUpList();
    });
    this.subscriptions.push(routeSubscription);
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
  loadFollowUpList() {
    const queryParams = new QueryParamsModel(
      this.filterConfiguration(),
      this.sort.direction !== '' ? this.sort.direction : 'desc',
      !this.sort.active || this.sort.active === '' ? 'created' : this.sort.active,
      this.paginator.pageIndex,
      this.paginator.pageSize,
    );
    this.store.dispatch(new FollowUpPageRequested({ page: queryParams }));
  }

  editFollowup(row) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.closeOnNavigation = true;
    dialogConfig.autoFocus = true;
    dialogConfig.width = '50vw';
    const id = row.transformation_id ? row.transformation_id : null;
    dialogConfig.data = {
      mode: 'UPDATE',
      body: row,
      type: row.transaction_type,
      title: '',
      body_id: id,
      id,
      followup: row,
      calendar: true,
    };
    const dialogRef = this.dialog.open(FollowUpDialogComponent, dialogConfig);
    dialogRef.afterClosed().subscribe(() => {
      setTimeout(() => {
        this.loadFollowUpList();
      });
    });
  }

  private filterConfiguration(): any {
    const filter: any = {};
    if (this.userSelected && this.userSelected.id) {
      filter.responsible_user_id = this.userSelected.id;
    }
    if (this.filterStatus !== undefined) {
      filter.completed_followup = this.filterStatus;
    }
    if (this.teamsFollowUp) {
      filter.team_followups = this.teamsFollowUp;
    }
    if (
      this.minDate &&
      this.maxDate &&
      moment.isDate(this.maxDate) &&
      moment.isDate(this.minDate)
    ) {
      filter.from_date = moment(this.minDate).format('YYYY-MM-DD');
      filter.to_date = moment(this.maxDate).format('YYYY-MM-DD');
    }
    return filter;
  }

  clearDateFilterCreated() {
    if (!this.maxDate && !this.minDate) {
      return;
    }
    this.maxDate = null;
    this.minDate = null;
    this.startDateCreated.setValue(null);
    this.toDateCreated.setValue(null);
    this.resetPagination();
    this.loadFollowUpList();
  }

  setMaxDateCreated($event: MatDatepickerInputEvent<any>) {
    this.maxDate = $event.value;
    if (this.maxDate && this.minDate) {
      this.resetPagination();
      this.loadFollowUpList();
    }
  }

  setMinDateCreated($event: MatDatepickerInputEvent<any>) {
    this.minDate = $event.value;
    if (this.maxDate && this.minDate) {
      this.resetPagination();
      this.loadFollowUpList();
    }
  }

  resetPagination() {
    this.paginator.pageIndex = 0;
    this.paginator.pageSize = 10;
  }

  selectedUser(value: any) {
    this.userSelected = value;
    this.userEle.nativeElement.value = value ? value.name : '';
    this.loadFollowUpList();
  }

  private userAutoComplete(term: string) {
    this.userService.searchUsers(term).subscribe((res) => {
      if (!res) {
        this.userOptions = [];
        return;
      }
      this.userOptions = res.data;
      this.cdr.detectChanges();
    });
  }

  openComments(ele) {
    this.followUpService.getFollowupComments(ele.id).subscribe((res) => {
      if (res && res.data) {
        this.dialog.open(CommentsDialogComponent, {
          data: {
            comments: res.data,
            transactionId: ele.transformation_id,
            transactionType: ele.transaction_type,
            height: 55,
            followup_id: ele.id,
          },
          width: '600px',
        });
      }
    });
  }
}
